import { useLingui } from '@lingui/react/macro';
import {
  buildAutoslideLoginUrlWithNavop,
  buildAutoslideLoginUrlWithRecordUrl,
  buildMobileDeepLinkUrl,
  type NavopValue,
} from '@sit/intacct-web';
import ErrorBoundary from '@web/components/ErrorBoundary';
import { intacctUrls } from '@web/config/env';
import { ErrorFallback } from '@web/containers/EntriesApprovals/Error';
import { useFragmentParams } from '@web/helpers/routing';
import { Suspense, useEffect } from 'react';

function useDeepLinkFragments() {
  const { t } = useLingui();
  const memoizedP = useFragmentParams();
  const p = new URLSearchParams(memoizedP.toString()); // Avoid issues with URLSearchParams being reused across renders with the React compiler
  const navopStr = p.get('navop');
  const companyId = p.get('companyId');
  const loginId = p.get('loginId');
  const r = p.get('r'); // `r` in: https://www.intacct.com/ia/acct/ur.phtml?.r=s8mBYsNwpUxvbWbNJ9Oj-Vom1OBMzgynEbFNYHhdodf
  const isEmbedded = p.get('isembedded');
  if (!companyId) {
    throw new Error(t`Missing required fragment parameters`);
  }
  const navop = navopStr ? Number(navopStr) : undefined;
  if (Number.isNaN(navop)) {
    throw new Error(t`Invalid navop value: ${navopStr}`);
  }
  p.delete('navop');
  p.delete('companyId');
  p.delete('loginId');
  p.delete('r');
  p.delete('isembedded');

  return {
    navop: navop as NavopValue | undefined,
    companyId,
    loginId,
    fragmentParams: p,
    r,
    isEmbedded,
  };
}

/**
 * Custom hook that navigates to a deep link based on the platform.
 *
 * This hook attempts to navigate to a mobile deep link immediately. If the navigation
 * to the mobile deep link fails within 50 milliseconds, it falls back to navigating
 * to a web deep link.
 *
 * @param mobileDeepLink - The URL for the mobile deep link.
 * @param webDeepLink - The URL for the web deep link.
 *
 * @example
 * useNavigateToDeepLink('myapp://path', 'https://myapp.com/path');
 */
function useNavigateToDeepLink(mobileDeepLink: string, webDeepLink: string) {
  useEffect(() => {
    const now = Date.now();
    const timeout = setTimeout(() => {
      if (Date.now() - now > 100) return;
      window.location.href = webDeepLink;
    }, 50);
    window.location.href = mobileDeepLink;
    return () => clearTimeout(timeout);
  }, [mobileDeepLink, webDeepLink]);
}

function useDeepLink() {
  const { navop, companyId, loginId, r, isEmbedded, fragmentParams } = useDeepLinkFragments();
  const mobileDeepLink = buildMobileDeepLinkUrl({
    navop,
    params: fragmentParams,
  });
  const intacctDeepLink = r
    ? buildAutoslideLoginUrlWithRecordUrl(r, isEmbedded, intacctUrls.ur)
    : buildAutoslideLoginUrlWithNavop(companyId, loginId, navop, isEmbedded, intacctUrls.login); // TODO: handle additional query params
  useNavigateToDeepLink(mobileDeepLink, intacctDeepLink);
}

function DeepLinking() {
  useDeepLink();
  return null;
}

export function Component() {
  return (
    <ErrorBoundary fallbackUI={(error) => <ErrorFallback error={error} />}>
      <Suspense fallback={null}>
        <DeepLinking />
      </Suspense>
    </ErrorBoundary>
  );
}

Component.displayName = 'DeepLinking';
